import { UsageByMonth } from "../model/analytics.types";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { getErrorMessage } from "@/shared/lib/utils";
import { AxiosError } from "axios";

interface Props {
  usage: UsageByMonth[];
  loading?: boolean;
  error?: AxiosError | null;
}

// Função para formatar a data do mês
const formatMonth = (monthStr: string) => {
  try {
    const [year, month] = monthStr.split('-');
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const monthIndex = parseInt(month) - 1;
    return `${monthNames[monthIndex]}/${year.slice(-2)}`;
  } catch {
    return monthStr;
  }
};

export function AnalyticsChart({ usage, loading, error }: Props) {

  if (error) {
    // Safely extract error message with proper type handling
    console.error('AnalyticsChart error:', error);

    return (
      <div className="bg-[#232323] rounded-lg p-6 mb-8">
        <h2 className="text-2xl font-semibold mb-4">Utilizações por Mês</h2>
        <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-4">
          <p className="text-red-400">Erro ao carregar gráfico: {getErrorMessage(error)}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-[#232323] rounded-lg p-6 mb-8">
        <h2 className="text-2xl font-semibold mb-4">Utilizações por Mês</h2>
        <div className="w-full h-[320px] bg-white/5 rounded animate-pulse flex items-center justify-center">
          <div className="text-white/40">Carregando gráfico...</div>
        </div>
      </div>
    );
  }

  const sanitizedUsage = [...(usage ?? [])];
  while (sanitizedUsage.length > 0) {
    const lastItem = sanitizedUsage[sanitizedUsage.length - 1];
    const paid = Number(lastItem?.paid ?? 0);
    const free = Number(lastItem?.free ?? 0);
    const total = Number(lastItem?.total ?? paid + free);
    if (total > 0) {
      break;
    }
    sanitizedUsage.pop();
  }

  if (!sanitizedUsage || sanitizedUsage.length === 0) {
    return (
      <div className="bg-[#232323] rounded-lg p-6 mb-8">
        <h2 className="text-2xl font-semibold mb-4">Utilizações por Mês</h2>
        <div className="w-full h-[320px] bg-white/5 rounded flex items-center justify-center">
          <div className="text-white/40">Nenhum dado disponível para o período selecionado</div>
        </div>
      </div>
    );
  }

  // Processar dados para formatar as datas
  const formattedUsage = sanitizedUsage.map(item => ({
    ...item,
    formattedMonth: formatMonth(item.month),
    originalMonth: item.month
  }));

  return (
    <div className="bg-[#232323] rounded-lg p-6 mb-8">
      <h2 className="text-2xl font-semibold mb-4 ">Utilizações por Mês</h2>
      <div className="w-full max-w-full">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart 
            data={formattedUsage} 
            barCategoryGap={20} 
            barGap={4}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid
              vertical={false}
              stroke="#333"
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="formattedMonth"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              tick={{ fill: "#fff", fontSize: 12 }}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: "#fff", fontSize: 14 }}
              domain={[0, 'dataMax']}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1F1F1F',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#fff'
              }}
              labelFormatter={(label, payload) => {
                if (payload && payload[0] && payload[0].payload) {
                  return `Mês: ${payload[0].payload.originalMonth}`;
                }
                return `Mês: ${label}`;
              }}
            />
            <Legend />
            <Bar
              dataKey="free"
              fill="#10B981"
              radius={[4, 4, 0, 0]}
              name="Presets Gratuitos"
              barSize={16}
            />
            <Bar
              dataKey="paid"
              fill="#3B82F6"
              radius={[4, 4, 0, 0]}
              name="Presets Pagos"
              barSize={16}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
